import React, { useEffect } from 'react';
import { Outlet, useParams, useSearchParams } from 'react-router-dom';
import { SafeSuspense } from './app/components/error-boundary/SafeSuspense';
import { FullPageLoadingSpinner } from './app/components/loading/FullPageLoadingSpinner';
import {
	useAppDispatch,
	useAppSelector,
} from './app/modules/redux/hooks/StoreHook';
import { setLanguage } from './app/modules/redux/features/localization/LocalizationSlice';
import { LanguagesTypes } from './app/modules/core/components/language-selector/LanguagesTypes';
import { CountriesType } from './app/modules/core/components/language-selector/CountriesType';
import { errors$ } from './app/modules/core/http-clients/ApiHttpClient';
import { IError } from './app/modules/core/interfaces/IError';
import {
	setDocument,
	setUserType,
} from './app/modules/redux/features/User/UserSlice';
import { BuildDocumentType } from './app/modules/core/utils/MaskDocuments';
import { UserTypes } from './app/modules/auth/enums/UserTypes';
import { hotjar } from 'react-hotjar';
import { UpdateNotification } from './app/components/update-notification/UpdateNotification';

import TagManager from 'react-gtm-module';
import { ToastWrapper } from './app/modules/core/components/toasts/ToastWrapper';
import { toggleToast } from './app/modules/redux/features/Toast/ToastSlice';
import { ToastStatus } from './app/modules/core/components/toasts/ToastStatus';

const AsyncLayoutModalComponent = React.lazy(
	async () =>
		await import('./app/modules/core/components/modals/LayoutModalComponent')
);

const AsyncLayoutSidebarComponent = React.lazy(
	async () =>
		await import('./app/modules/core/components/sidebars/LayoutSideBar')
);

export const RootOutlet: React.FunctionComponent<any> = (props: any) => {
	const { language, country } = useParams();
	const errorHandler$ = errors$.getSubject();
	const dispatch = useAppDispatch();
	const [searchParams] = useSearchParams();

	// Store redux
	const localization = useAppSelector(state => state.localization);

	useEffect(() => {
		TagManager.initialize({ gtmId: process.env.GTM_ID as any });
		const userType = searchParams.get('user_type');
		if (country) {
			const documentType = BuildDocumentType[
				country?.toUpperCase() as CountriesType
			](userType as UserTypes);
			dispatch(
				setLanguage({
					language:
						localization.language ??
						(language?.toUpperCase() as LanguagesTypes),
					country:
						localization.country ?? (country?.toUpperCase() as CountriesType),
				})
			);
			dispatch(setDocument(documentType));

			dispatch(setUserType(userType as UserTypes));
		}
	}, []);

	useEffect(() => {
		errorHandler$.subscribe((error: IError) => {
			dispatch(
				toggleToast({
					open: true,
					text: error.toast.title as string,
					status: ToastStatus.ERROR,
					autoClose: true,
					closeTimeout: 5400,
				})
			);
		});
	}, []);

	useEffect(() => {
		hotjar.initialize(process.env.HOTJAR_ID as any, 6);
	}, []);

	return (
		<>
			<ToastWrapper />
			<Outlet />
			<UpdateNotification />
			<SafeSuspense fallback={<FullPageLoadingSpinner />}>
				<AsyncLayoutModalComponent />
				<AsyncLayoutSidebarComponent />
			</SafeSuspense>
		</>
	);
};
